import { A, useNavigate, useParams, useSearchParams } from "@solidjs/router";
import { createEffect, createSignal, Show } from "solid-js";
import type {
	EvaluationRequest,
	KnockoutMode,
	Report,
	ReportSnapshot,
} from "../../api/domain.ts";
import { useApi } from "../../api/provider.tsx";
import { describeError } from "../../api/result.ts";
import { Async } from "../../components/Async.tsx";
import { deleteColumn } from "../../components/DeleteButton.tsx";
import {
	Actions,
	Button,
	DownloadLink,
	Form,
	SelectField,
	TextField,
} from "../../components/Form.tsx";
import { Panel } from "../../components/Panel.tsx";
import { Table } from "../../components/Table.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader, loadedValue } from "../../lib/async.ts";
import { formatMoment } from "../../lib/format.ts";
import { useSession } from "../../session/provider.tsx";
import styles from "./admin.module.scss";
import { OwnerTag } from "./CustomersPage.tsx";
import { historyHref } from "./history.ts";
import { ReportReading, reportSectionOf } from "./ReportView.tsx";

const knockoutModes: KnockoutMode[] = ["enforce", "annotate", "ignore"];

const mergeOrders: string[][] = [["human", "ai"], ["human"], ["ai", "human"]];

/** The choices the builder holds before anything is evaluated or persisted. */
type Choices = {
	view: string;
	primary: string;
	merge: number;
	knockout: KnockoutMode;
	asOf: string;
};

const requestOf = (choices: Choices): EvaluationRequest => ({
	view: choices.view,
	primary_questionnaire: choices.primary === "" ? null : choices.primary,
	merge_order: mergeOrders[choices.merge] ?? ["human", "ai"],
	knockout_mode: choices.knockout,
	as_of: choices.asOf === "" ? null : choices.asOf,
	// The report belongs to whoever the view belongs to; the server fills it in.
	customer: null,
});

/**
 * The report builder. Every change is explorable through `POST /evaluate`, which
 * persists nothing, so the choices can be tried before a report is created.
 */
export const ReportsPage = () => {
	const api = useApi();
	const i18n = useI18n();

	const [params] = useSearchParams<{ as_of?: string; view?: string }>();

	const views = createLoader(() => api.list("views", { lang: i18n.locale() }));
	const questionnaires = createLoader(() => api.list("questionnaires"));
	const reports = createLoader(() => api.list("reports"));
	const session = useSession();
	const customers = createLoader(() =>
		session.canWrite()
			? api.list("customers")
			: Promise.resolve({ ok: true as const, value: [] }),
	);

	const [choices, setChoices] = createSignal<Choices>({
		view: params.view ?? "",
		primary: "",
		merge: 0,
		knockout: "annotate",
		asOf: params.as_of ?? "",
	});
	const [jumped, setJumped] = createSignal(false);
	const [snapshot, setSnapshot] = createSignal<ReportSnapshot | undefined>(
		undefined,
	);
	const [problem, setProblem] = createSignal<string | undefined>(undefined);
	const [busy, setBusy] = createSignal(false);
	const navigate = useNavigate();
	let preview: HTMLDivElement | undefined;

	const evaluate = async () => {
		if (choices().view === "") return;
		setBusy(true);
		setProblem(undefined);
		const result = await api.evaluate(requestOf(choices()));
		setBusy(false);
		if (!result.ok) return setProblem(describeError(i18n.t(), result.error));
		setSnapshot(result.value);
		// The preview renders below a tall form, so a run that left the viewport
		// unchanged would read as a button that does nothing. Absent in tests.
		requestAnimationFrame(() =>
			preview?.scrollIntoView?.({ behavior: "smooth", block: "start" }),
		);
	};

	/** Saving lands on the saved report: the very page the customer will open. */
	const persist = async () => {
		setBusy(true);
		const result = await api.createReport(requestOf(choices()));
		setBusy(false);
		if (!result.ok) return setProblem(describeError(i18n.t(), result.error));
		navigate(`/reports/${result.value.id}`);
	};

	/**
	 * A history jump arrives as `?as_of=`, so the builder evaluates that moment on
	 * its own: the reader asked for a past state, not for a form to fill in.
	 */
	createEffect(() => {
		if (jumped() || (params.as_of ?? "") === "") return;
		const rows = loadedValue(views.value());
		if (rows === undefined) return;

		const view = choices().view === "" ? (rows[0]?.id ?? "") : choices().view;
		if (view === "") return;

		setJumped(true);
		setChoices({ ...choices(), view });
		void evaluate();
	});

	return (
		<>
			<div class={styles.heading}>
				<h1>{i18n.t().reports.title}</h1>
			</div>

			<Show when={session.canWrite()}>
				<Panel title={i18n.t().reports.builder}>
					<Async value={views.value()} onRetry={views.reload}>
						{(viewRows) => (
							<Async
								value={questionnaires.value()}
								onRetry={questionnaires.reload}
							>
								{(questionnaireRows) => (
									<Form>
										<Show when={viewRows.length === 0}>
											<p class={styles.problem}>
												{i18n.t().views.none}{" "}
												<A href="/views">{i18n.t().views.newView}</A>
											</p>
										</Show>
										<SelectField
											label={i18n.t().reports.view}
											placeholder={i18n.t().common.none}
											choices={viewRows.map((view) => ({
												id: view.id,
												label: view.label,
											}))}
											value={choices().view}
											onPick={(view) => setChoices({ ...choices(), view })}
										/>
										{/* The report belongs to the view's customer, so the owner is shown, not picked. */}
										<Show
											when={viewRows.find((view) => view.id === choices().view)?.customer}
										>
											{(owner) => (
												<p class={styles.note}>
													{i18n.t().common.customer}:{" "}
													<OwnerTag
														customers={loadedValue(customers.value())}
														owner={owner()}
													/>
												</p>
											)}
										</Show>
										<SelectField
											label={i18n.t().reports.primaryQuestionnaire}
											placeholder={i18n.t().common.none}
											optional
											choices={questionnaireRows.map((entry) => ({
												id: entry.id,
												label: entry.id,
											}))}
											value={choices().primary}
											onPick={(primary) =>
												setChoices({ ...choices(), primary })
											}
										/>
										<SelectField
											label={i18n.t().reports.mergeOrder}
											hint={i18n.t().reports.mergeOrderHint}
											choices={mergeOrders.map((order, index) => ({
												id: String(index),
												label: order.join(" → "),
											}))}
											value={String(choices().merge)}
											onPick={(merge) =>
												setChoices({ ...choices(), merge: Number(merge) })
											}
										/>
										<SelectField
											label={i18n.t().reports.knockoutMode}
											choices={knockoutModes.map((mode) => ({
												id: mode,
												label:
													mode === "enforce"
														? i18n.t().reports.knockoutEnforce
														: mode === "ignore"
															? i18n.t().reports.knockoutIgnore
															: i18n.t().reports.knockoutAnnotate,
											}))}
											value={choices().knockout}
											onPick={(knockout) =>
												setChoices({
													...choices(),
													knockout: knockout as KnockoutMode,
												})
											}
										/>
										<TextField
											label={i18n.t().reports.asOf}
											hint={i18n.t().reports.asOfHint}
											optional
											placeholder="2026-08-20T10:00:00Z"
											value={choices().asOf}
											onInput={(asOf) => setChoices({ ...choices(), asOf })}
										/>
										<Show when={problem() !== undefined}>
											<p class={styles.problem}>{problem()}</p>
										</Show>
										<Actions>
											<Button
												label={i18n.t().reports.evaluateNow}
												tone="primary"
												disabled={busy() || choices().view === ""}
												onClick={() => void evaluate()}
											/>
										</Actions>
									</Form>
								)}
							</Async>
						)}
					</Async>
				</Panel>
			</Show>

			{/*
			  The preview is the same reading the saved report page renders, under
			  the same head line, so what is checked here is what gets saved.
			*/}
			<Show when={snapshot()}>
				{(loaded) => (
					<div class={styles.preview} ref={preview}>
						<div class={styles.heading}>
							<h2>{i18n.t().reports.preview}</h2>
							<p class={styles.lede}>{i18n.t().reports.previewHint}</p>
						</div>
						<ReportReading
							snapshot={loaded()}
							label={i18n.t().reports.preview}
							actions={
								<Button
									label={i18n.t().reports.persist}
									tone="primary"
									disabled={busy()}
									onClick={() => void persist()}
								/>
							}
						/>
					</div>
				)}
			</Show>

			<Panel
				title={session.canWrite() ? i18n.t().reports.saved : i18n.t().reports.title}
			>
				<Async value={reports.value()} onRetry={reports.reload}>
					{(rows) => (
						<Table
							rows={rows}
							rowKey={(report) => report.id}
							emptyLabel={i18n.t().common.empty}
							columns={[
								{
									key: "id",
									header: i18n.t().common.identity,
									cell: (report) => (
										<A href={`/reports/${report.id}`}>{report.id}</A>
									),
								},
								{
									key: "view",
									header: i18n.t().reports.view,
									cell: (report) => report.view,
								},
								...(session.isCustomer()
									? []
									: [
											{
												key: "customer",
												header: i18n.t().common.customer,
												cell: (report: Report) => (
													<OwnerTag
														customers={loadedValue(customers.value())}
														owner={report.customer}
													/>
												),
											},
										]),
								{
									key: "asOf",
									header: i18n.t().reports.asOf,
									cell: (report) => formatMoment(report.as_of, i18n.locale()),
								},
								{
									key: "locked",
									header: i18n.t().reports.locked,
									cell: (report) =>
										report.locked_at === null
											? i18n.t().reports.live
											: formatMoment(report.locked_at, i18n.locale()),
								},
								...(session.canWrite()
									? [
											deleteColumn({
												header: i18n.t().common.actions,
												collection: "reports",
												id: (report) => report.id,
												onRemoved: reports.reload,
											}),
										]
									: []),
							]}
						/>
					)}
				</Async>
			</Panel>
		</>
	);
};

/**
 * One saved report, for the consultant and the customer alike. The consultant
 * additionally gets to lock it and take the workbook; the server refuses both
 * to a customer. The open section rides in the URL, so a pasted link lands on it.
 */
export const ReportPage = () => {
	const api = useApi();
	const i18n = useI18n();
	const params = useParams<{ id: string }>();
	const [query, setQuery] = useSearchParams<{ section?: string }>();
	const session = useSession();

	/**
	 * `GET /reports/{id}` answers the result tree, not the stored row, so the
	 * snapshot is the only thing to load: it names its report and says whether
	 * that report is locked.
	 */
	const evaluation = createLoader(() => api.reportEvaluation(params.id));
	/*
	 * The row and the view label are what the page is titled with. Both are
	 * optional reads: the identifier stands in until they arrive or if they fail.
	 */
	const rows = createLoader(() => api.list("reports"));
	const views = createLoader(() => api.list("views", { lang: i18n.locale() }));
	const viewLabel = (): string | undefined => {
		const row = loadedValue(rows.value())?.find((report) => report.id === params.id);
		if (row === undefined) return undefined;
		return loadedValue(views.value())?.find((view) => view.id === row.view)?.label ?? row.view;
	};
	const [busy, setBusy] = createSignal(false);

	const lock = async () => {
		setBusy(true);
		await api.lockReport(params.id);
		setBusy(false);
		evaluation.reload();
	};

	return (
		<Async value={evaluation.value()} onRetry={evaluation.reload}>
			{(snapshot) => (
				<>
					<div class={styles.heading}>
						<h1>{viewLabel() ?? snapshot.report}</h1>
						<p class={styles.lede}>
							{i18n.t().reports.title} <span class={styles.mono}>{snapshot.report}</span>
						</p>
						<A href={historyHref("report", snapshot.report)}>
							{i18n.t().history.link}
						</A>
					</div>
					<ReportReading
						snapshot={snapshot}
						label={snapshot.report}
						section={reportSectionOf(query.section)}
						onPick={(section) =>
							setQuery({ section: section === "ranking" ? "" : section })
						}
						actions={
							<Show when={session.canWrite()}>
								<Button
									label={i18n.t().reports.lock}
									tone="primary"
									disabled={busy() || snapshot.locked_at !== null}
									onClick={() => void lock()}
								/>
								<DownloadLink
									label={i18n.t().reports.downloadWorkbook}
									href={api.workbookUrl("reports", snapshot.report, {
										lang: i18n.locale(),
									})}
									filename={`${snapshot.report}-${i18n.locale()}.xlsx`}
								/>
							</Show>
						}
					/>
				</>
			)}
		</Async>
	);
};
